import { useState, useEffect, useRef } from "react";
import { jwtFetch } from "./utils/apiClient";
import { format } from "date-fns";
import { Tabs, Tab, Modal } from "react-bootstrap";
import { config, priceFormat } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Attainment = () => {
  const [ attainment, setAttainment ] = useState(null);
  const [ tableFilters, setTableFilters ] = useState(null);
  const [ tableSearch, setTableSearch ] = useState('');
  const [ tableSortBy, setTableSortBy ] = useState('attainment_code');
  const [ tableSortType, setTableSortType ] = useState('descending');
  const [ showFilter, setShowFilter ] = useState(false);
  const [ filters, setFilters ] = useState([{field: '', value: ''}]);
  const [ isLoadingPage, setIsLoadingPage ] = useState(true);
  const searchInputRef = useRef(null);

  const crmUser = JSON.parse(localStorage.getItem('crmUser'));

  useEffect(() => {
    setTableFilters({
      attainmentStatus: 'Submitted',
      attainmentCreatedBy: crmUser.user_level === 'User' && crmUser.user_department === 'Sales' ? [crmUser.user_keyid] : []
    });
    getAttainment();
  }, []);

  const getAttainment = async () => {
    try {
      const requestAttainment = {};
      const fetchAttainment = await jwtFetch(config.ENDPOINT + 'get/attainment', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestAttainment)
      });
      const responseAttainment = await fetchAttainment.json();
      if (responseAttainment.data) {
        for (let i in responseAttainment.data) {
          let searchData = String(responseAttainment.data[i].attainment_code ?? '').toLowerCase();
          searchData += String(responseAttainment.data[i].attainment_total ?? '').toLowerCase();
          searchData += String(responseAttainment.data[i].attainment_remarks ?? '').toLowerCase();
          searchData += String(responseAttainment.data[i].attainment_approval ?? '').toLowerCase();
          searchData += String(responseAttainment.data[i].attainment_status ?? '').toLowerCase();
          searchData += String(responseAttainment.data[i].approval_remarks ?? '').toLowerCase();
          searchData += String(responseAttainment.data[i].company_name ?? '').toLowerCase();
          searchData += responseAttainment.data[i].target_year;
          searchData += responseAttainment.data[i].target_quarter;
          searchData += String(responseAttainment.data[i].sales_name ?? '').toLowerCase();
          responseAttainment.data[i].search = searchData;
        }
        setAttainment(responseAttainment.data);
        setIsLoadingPage(false);
      } else {
        alert(responseAttainment.message);
      }
    } catch (error) {
      console.error('Error in getAttainment:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const filterTable = (key, value) => {
    let filters = {...tableFilters};
    if (key === 'status') {
      filters.attainmentStatus = value;
    }
    if (key === 'createdBy') {
      if (filters.attainmentCreatedBy.includes(value)) {
        filters.attainmentCreatedBy = filters.attainmentCreatedBy.filter(item => item !== value);
      } else {
        filters.attainmentCreatedBy.push(value);
      }
    }
    setTableFilters(filters);
  }

  const sortTable = (sortBy) => {
    if (tableSortBy === sortBy) {
      setTableSortType(tableSortType === 'ascending' ? 'descending' : 'ascending');
    } else {
      setTableSortBy(sortBy);
      setTableSortType('ascending');
    }
  }

  const clearSearch = () => {
    setTableSearch('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }
  
  const getStatusCount = (status) => {
    if (!attainment) return 0;
    return attainment.filter(attainment => 
      attainment.attainment_status === status && 
      (tableSearch === '' || attainment.search.includes(tableSearch.toLowerCase())) &&
      (filters.length === 0 || filters.every(filter => {
        if (!filter.field || !filter.value) return true;
        const fieldValue = attainment[filter.field];
        if (fieldValue === null || fieldValue === undefined) return false;
        const fieldString = String(fieldValue).toLowerCase();
        const filterString = String(filter.value).toLowerCase();
        return fieldString.includes(filterString);
      })) &&
      (crmUser.user_level === 'User' && crmUser.user_department === 'Sales' ? 
        [crmUser.user_keyid].includes(attainment.created_by) : 
        true
      )
    ).length;
  }

  const changeFilter = (index, type, value) => {
    const updated = [...filters];
    updated[index][type] = value;
    setFilters(updated);
  }

  const addFilter = () => {
    const currentFilters = [...filters];
    currentFilters.push({ field: '', value: '' });
    setFilters(currentFilters);
  }

  const removeFilter = (index) => {
    const updated = [...filters];
    updated.splice(index, 1);
    setFilters(updated);
  }

  const resetFilter = () => {
    setFilters([{field:'', value: ''}]);
  }

  const generateXls = () => {
    const headers = ["#", "Attainment Number", "Status", "Created Date", "Quotation Number", "Total", "Quarter", "Company Group", "Subsidiary", "Sales Name"];
    const data = attainment.map((attainment, s) => [
      s + 1,
      attainment.attainment_code,
      attainment.attainment_status.replace('Canceled', 'Cancelled'),
      format(attainment.created_on, 'd MMM yyyy'),
      attainment.quotation_code,
      attainment.attainment_total,
      "Q" + attainment.target_quarter + " " + attainment.target_year,
      attainment.company_name,
      attainment.subcompany_name,
      attainment.sales_name,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'});
    saveAs(blob, "attainment.xlsx");
  }

  return (
    <div id="attainment-list">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar"/></a>
            </div>
            <hr className="m-0"/>
            <Menu active="attainment"/>
          </div>
          <div className="col-md-10">
            <Header/>
            <hr className="m-0"/>
            <div className="main">
              <div className="p-4">

                <section className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="fs-24">Attainment List</div>
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <div className="input-group input-search">
                        <button className="btn bg-white" type="button" onClick={() => clearSearch()}>{tableSearch ? <i className="bi bi-x-lg"/> : <i className="bi bi-search"/>}</button>
                        <input className="form-control" type="text" placeholder="Search" value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} ref={searchInputRef} autoFocus/>
                      </div>
                      <button className="btn btn-outline-secondary" type="button" onClick={() => setShowFilter(true)}><i className="bi bi-filter me-1"/> Filter {filters.filter(f => f.field && f.value).length > 0 ? `(${filters.filter(f => f.field && f.value).length})` : ''}</button>
                      <button className="btn btn-link bg-gold" type="button" onClick={() => generateXls()}><i className="bi bi-download me-1"/> Export</button>
                    </div>
                  </div>
                  <div className="bg-white border-radius-4 p-4">
                    <Tabs className="mb-3" defaultActiveKey="Submitted" onSelect={(key) => filterTable('status', key)}>
                      <Tab eventKey="Submitted" title={`Submitted${getStatusCount('Submitted') > 0 ? ` (${getStatusCount('Submitted')})` : ''}`} />
                      <Tab eventKey="Approved" title={`Approved${getStatusCount('Approved') > 0 ? ` (${getStatusCount('Approved')})` : ''}`} />
                      <Tab eventKey="Rejected" title={`Rejected${getStatusCount('Rejected') > 0 ? ` (${getStatusCount('Rejected')})` : ''}`} />
                      <Tab eventKey="Canceled" title={`Cancelled${getStatusCount('Canceled') > 0 ? ` (${getStatusCount('Canceled')})` : ''}`} />
                    </Tabs>
                    {attainment && attainment.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th className="bg-gold w-40">#</th>
                              <th className="bg-gold w-140 pointer" onClick={() => sortTable('attainment_code')}>Number {tableSortBy === 'attainment_code' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold w-140 pointer" onClick={() => sortTable('quotation_code')}>Quotation Number {tableSortBy === 'quotation_code' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold pointer text-start" onClick={() => sortTable('company_name')}>Company Name {tableSortBy === 'company_name' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold w-80 pointer" onClick={() => sortTable('target_quarter')}>Quarter {tableSortBy === 'target_quarter' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold w-80 pointer" onClick={() => sortTable('target_year')}>Year {tableSortBy === 'target_year' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold w-125 pointer text-end" onClick={() => sortTable('attainment_total')}>Amount {tableSortBy === 'attainment_total' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold w-150 pointer text-start" onClick={() => sortTable('sales_name')}>Sales Name {tableSortBy === 'sales_name' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold w-40"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...attainment]
                              .sort((a, b) => {
                                const aValue = a[tableSortBy];
                                const bValue = b[tableSortBy];
                                const aNum = Number(aValue);
                                const bNum = Number(bValue);
                                const isNumeric = !isNaN(aNum) && !isNaN(bNum);
                                if (isNumeric) {
                                  return tableSortType === 'ascending' ? aNum - bNum : bNum - aNum;
                                }
                                return tableSortType === 'ascending'
                                  ? (aValue ?? '').toString().localeCompare((bValue ?? '').toString())
                                  : (bValue ?? '').toString().localeCompare((aValue ?? '').toString());
                              })
                              .filter(attainment => attainment.search.includes(tableSearch.toLowerCase()))
                              .filter(attainment => {
                                if (tableFilters && tableFilters.attainmentStatus) {
                                  return attainment.attainment_status === tableFilters.attainmentStatus;
                                }
                                return true;
                              })
                              .filter(attainment => {
                                if (tableFilters) {
                                  if (tableFilters.attainmentCreatedBy && tableFilters.attainmentCreatedBy.length > 0) {
                                    return tableFilters.attainmentCreatedBy.includes(attainment.created_by);
                                  }
                                  return true;
                                }
                                return true;
                              })
                              .filter(attainment => {
                                if (filters && filters.length > 0) {
                                  return filters.every(filter => {
                                    if (!filter.field || !filter.value) return true;
                                    const fieldValue = attainment[filter.field];
                                    if (fieldValue === null || fieldValue === undefined) return false;
                                    const fieldString = String(fieldValue).toLowerCase();
                                    const filterString = String(filter.value).toLowerCase();
                                    return fieldString.includes(filterString);
                                  });
                                }
                                return true;
                              })
                              .map((attainment, s) => (
                              <tr key={"attainment-" + s}>
                                <td valign="middle" align="center">{s + 1}</td>
                                <td valign="middle" align="center">{attainment.attainment_code}</td>
                                <td valign="middle" align="center">{attainment.quotation_code}</td>
                                <td valign="middle">
                                  <strong>{attainment.company_name}</strong>
                                  <br/>
                                  <small>{attainment.subcompany_name}</small>
                                </td>
                                <td valign="middle" align="center">{"Q" + attainment.target_quarter}</td>
                                <td valign="middle" align="center">{attainment.target_year}</td>
                                <td valign="middle" align="right">{priceFormat(attainment.attainment_total, 'IDR')}</td>
                                <td valign="middle">
                                  {attainment.sales_name}
                                  <br/>
                                  <small>Created on {format(attainment.created_on, 'd MMM yyyy')}</small>
                                </td>
                                <td valign="middle" align="right"><a className="btn btn-sm bg-gold" href={'/attainment/detail/' + attainment.id_attainment}><i className="bi bi-eye"/></a></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p>{isLoadingPage ? 'Loading...' : 'No data'}</p>
                    )}
                  </div>
                </section>
                                
                <Modal show={showFilter} onHide={() => setShowFilter(false)}>
                  <Modal.Header closeButton>
                    <Modal.Title>Filter Table</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {filters.map((filter, f) => {
                      return (
                        <div className="row align-items-baseline form-group mb-2 g-2" key={"filter-" + f}>
                          <div className="col-md-4">
                            <select className="form-select" value={filter.field} onChange={(e) => changeFilter(f, 'field', e.target.value)} required>
                              <option value=""></option>
                              <option value="target_year">Year</option>
                              <option value="target_quarter">Quarter</option>
                              <option value="sales_name">Sales Name</option>
                              <option value="company_name">Company Name</option>
                              <option value="quotation_code">Quotation Number</option>
                            </select>
                          </div>
                          <div className="col-md-7">
                            <input className="form-control" type="text" value={filter.value} onChange={(e) => changeFilter(f, 'value', e.target.value)} required/>
                          </div>
                          <div className="col-md-1">
                            <button className="btn btn-sm btn-link" onClick={() => removeFilter(f)}><i className="bi bi-dash-lg"></i></button>
                          </div>
                        </div>
                      );
                    })}
                    <div className="d-flex justify-content-between">
                      <button className="btn btn-outline-secondary" type="button" onClick={() => addFilter()} disabled={isLoadingPage}>Add Filter</button>
                      <button className="btn btn-link" type="button" onClick={() => resetFilter()} disabled={isLoadingPage}>Reset</button>
                    </div>
                  </Modal.Body>
                </Modal>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attainment;


